// App.js
import React, { useEffect, useRef } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import SnackbarProvider from "@codejq/react-simple-snackbar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getCompany } from "./actions/companyActions";
import { WEBSOCKET_CONNECT, WEBSOCKET_DISCONNECT } from "./actions/constants";
import InitializingScreen from "./components/InitializingScreen/InitializingScreen";
import FirstLoginChangePassword from "./components/FirstLoginChangePassword/FirstLoginChangePassword";
import lngs from "./lngs";
import TopNav from "./components/TopNav/TopNav";
import AppBottomBar from "./components/AppBottomBar/AppBottomBar";
import RTL from "./utils/stylesprovider";
import { evaluateNumberExpr, isMathExpression } from "./utils/mathExpr";
import DateProvider from "./components/Shared/DateProvider";

// Import the routing component we created
import Routes from "./Routes";
import AppSubscriptionWrapper from "./components/Subscription/AppSubscriptionWrapper";

function App() {
  const { i18n } = useTranslation("operations");
  const dbInitializing = useSelector((state) => state.auth.dbInitializing);
  const mustChangePassword = useSelector((state) => state.auth.mustChangePassword);
  const user = JSON.parse(localStorage.getItem("profile"));
  const currentLang = i18n.resolvedLanguage;
  const lang = window.location.pathname.split("/")[1] || "en";
  const isInApp = window.location.pathname.includes("/app/");
  const isModernPOSTerminal = window.location.pathname.includes("/app/pos/terminal/");
  const { company } = useSelector((state) => state.companies);
  const dispatch = useDispatch();
  const socketInitialized = useRef(false);
  const userIdRef = useRef(null);

  // Example letter types (if you need them in app-level)
  const rentalLetterTypes = [
    "lease_extension",
    "eviction_notice",
    "sublease_agreement",
    // ...
  ];

  // Handle language changes
  useEffect(() => {
    if (currentLang !== lang && lngs[lang]?.nativeName?.length > 0) {
      i18n.changeLanguage(lang);
    }
  }, [currentLang, lang, i18n]);

  // Fetch company data
  useEffect(() => {
    if (user?.result?.companyId) {
      dispatch(getCompany(user.result.companyId));
    }
  }, [dispatch, user?.result?.companyId]);

  // Handle WebSocket connection
  useEffect(() => {
    const userId = user?.result?._id;

    if (
      userId &&
      (!socketInitialized.current || userId !== userIdRef.current)
    ) {
      console.log("Initializing WebSocket for user:", userId);
      socketInitialized.current = true;
      userIdRef.current = userId;

      dispatch({
        type: WEBSOCKET_CONNECT,
        payload: {
          token: user?.token,
          userId,
        },
      });

      return () => {
        console.log("Disconnecting WebSocket for user:", userIdRef.current);
        socketInitialized.current = false;
        dispatch({ type: WEBSOCKET_DISCONNECT });
      };
    }

    // If user logs out, disconnect
    if (!userId && socketInitialized.current) {
      console.log("User logged out, disconnecting WebSocket");
      socketInitialized.current = false;
      userIdRef.current = null;
      dispatch({ type: WEBSOCKET_DISCONNECT });
    }
  }, [dispatch, user?.result?._id, user?.token]);

  // Math expression evaluator — works on every input[type!="textarea"]
  // Number inputs: switched to text on focus so any operator can be typed
  // Evaluation triggers on blur or Enter, no "=" prefix required
  useEffect(() => {
    const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value").set;

    const tryEval = (el) => {
      const val = el.value.trim();
      if (!isMathExpression(val)) return;
      const result = evaluateNumberExpr(val);
      if (result === null) return;
      nativeSetter.call(el, result);
      el.dispatchEvent(new Event("input", { bubbles: true }));
    };

    const handleFocus = (e) => {
      const el = e.target;
      if (el.tagName === "INPUT" && el.type === "number") {
        el.dataset.wasNumber = "true";
        el.type = "text";
      }
    };

    const handleBlur = (e) => {
      const el = e.target;
      if (el.tagName !== "INPUT") return;
      tryEval(el);
      if (el.dataset.wasNumber) {
        delete el.dataset.wasNumber;
        el.type = "number";
      }
    };

    const handleKeyDown = (e) => {
      if (e.key !== "Enter") return;
      const el = e.target;
      if (el.tagName !== "INPUT") return;
      const val = el.value.trim();
      if (isMathExpression(val)) {
        const result = evaluateNumberExpr(val);
        if (result !== null) {
          e.preventDefault();
          nativeSetter.call(el, result);
          el.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
      if (el.dataset.wasNumber) {
        delete el.dataset.wasNumber;
        el.type = "number";
      }
    };

    document.addEventListener("focus", handleFocus, true);
    document.addEventListener("blur", handleBlur, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("focus", handleFocus, true);
      document.removeEventListener("blur", handleBlur, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // RTL or LTR setup
  const isLanguageRTL = lngs[i18n.resolvedLanguage]?.isRtl || false;
  useEffect(() => {
    document.documentElement.setAttribute("dir", isLanguageRTL ? "rtl" : "ltr");
  }, [isLanguageRTL]);

  if (dbInitializing) return <InitializingScreen />;

  return (
    <DateProvider>
      <RTL isRTL={isLanguageRTL}>
        <div dir={isLanguageRTL ? "rtl" : "ltr"}>
          <Router>
            <SnackbarProvider>
              {mustChangePassword ? (
                <FirstLoginChangePassword />
              ) : (
                <AppSubscriptionWrapper>
                  {/* Fixed teal navigation bar (app-only) */}
                  {user && <TopNav />}

                  {/* Spacer to push content below fixed Header + TopNav */}
                  {isInApp && user && !isModernPOSTerminal && (
                    <div className="app-top-spacer" />
                  )}

                  {/* All your routes go here */}
                  <Routes />

                  {/* Fixed bottom bar (app-only) */}
                  {user && <AppBottomBar />}
                </AppSubscriptionWrapper>
              )}
            </SnackbarProvider>
          </Router>
        </div>
      </RTL>
    </DateProvider>
  );
}

export default App;
